/**
 * Analytics utility functions for Google Analytics 4 integration
 *
 * All analytics functions check if analytics is enabled before sending events.
 * Analytics is disabled in development (localhost) unless explicitly enabled.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

/**
 * Check if analytics should be enabled
 * Returns false for localhost/development environments unless explicitly enabled
 */
export function isAnalyticsEnabled(): boolean {
  // Check if we're in a browser environment
  if (typeof window === 'undefined') return false;

  // Check if gtag is loaded
  if (!window.gtag) return false;

  // Check for explicit disable flag
  if (import.meta.env.VITE_ANALYTICS_DISABLED === 'true') return false;

  // Disable on localhost unless explicitly enabled
  const hostname = window.location.hostname;
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';

  if (isLocalhost && import.meta.env.VITE_ANALYTICS_DEBUG !== 'true') {
    return false;
  }

  return true;
}

/**
 * Get the Google Analytics Measurement ID from environment variables
 */
export function getGAMeasurementId(): string | undefined {
  return import.meta.env.VITE_GA_MEASUREMENT_ID;
}

/**
 * Track a page view
 * @param path - The page path (e.g., '/pricing')
 * @param title - The page title
 */
export function trackPageView(path: string, title?: string): void {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Page view (disabled):', { path, title });
    }
    return;
  }

  window.gtag?.('event', 'page_view', {
    page_path: path,
    page_title: title,
    page_location: window.location.href,
  });
}

/**
 * Track a custom event
 * @param category - Event category (e.g., 'CTA', 'Form', 'Navigation')
 * @param action - Event action (e.g., 'click', 'submit', 'view')
 * @param label - Optional event label for additional context
 * @param value - Optional numeric value associated with the event
 */
export function trackEvent(
  category: string,
  action: string,
  label?: string,
  value?: number
): void {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Event (disabled):', { category, action, label, value });
    }
    return;
  }

  window.gtag?.('event', action, {
    event_category: category,
    event_label: label,
    value: value,
  });
}

/**
 * Conversion types for form submissions and key actions
 */
export type ConversionType =
  | 'demo_request'
  | 'contact_form'
  | 'newsletter_signup'
  | 'pricing_cta_click'
  | 'phone_call_click'
  | 'free_trial_start';

/**
 * Track a conversion event
 * Used for important business actions like form submissions
 * @param conversionType - The type of conversion
 * @param value - Optional monetary or numeric value
 * @param additionalData - Optional additional data to include
 */
export function trackConversion(
  conversionType: ConversionType,
  value?: number,
  additionalData?: Record<string, unknown>
): void {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Conversion (disabled):', { conversionType, value, additionalData });
    }
    return;
  }

  // Map conversion types to GA4 event names
  const eventNames: Record<ConversionType, string> = {
    demo_request: 'generate_lead',
    contact_form: 'contact',
    newsletter_signup: 'sign_up',
    pricing_cta_click: 'begin_checkout',
    phone_call_click: 'contact',
    free_trial_start: 'sign_up',
  };

  window.gtag?.('event', eventNames[conversionType], {
    event_category: 'conversion',
    event_label: conversionType,
    value: value,
    currency: 'USD',
    ...additionalData,
  });

  // Also send as a custom conversion event for easier tracking
  window.gtag?.('event', `conversion_${conversionType}`, {
    value: value,
    currency: 'USD',
    ...additionalData,
  });
}

/**
 * Track CTA button clicks
 * @param ctaName - Name/identifier of the CTA (e.g., 'hero_request_demo', 'pricing_starter')
 * @param destination - The destination URL or action
 */
export function trackCTAClick(ctaName: string, destination?: string): void {
  trackEvent('CTA', 'click', ctaName);

  if (!isAnalyticsEnabled()) return;

  window.gtag?.('event', 'cta_click', {
    cta_name: ctaName,
    cta_destination: destination,
  });
}

/**
 * Track pricing tier selection
 * @param tierName - Name of the pricing tier (e.g., 'starter', 'professional', 'enterprise')
 * @param isAnnual - Whether annual billing was selected
 */
export function trackPricingSelection(tierName: string, isAnnual: boolean): void {
  trackEvent('Pricing', 'select_tier', tierName);

  if (!isAnalyticsEnabled()) return;

  window.gtag?.('event', 'select_item', {
    item_list_name: 'pricing_tiers',
    items: [{
      item_name: tierName,
      item_category: 'pricing',
      item_variant: isAnnual ? 'annual' : 'monthly',
    }],
  });
}

/**
 * Track outbound link clicks
 * @param url - The external URL being clicked
 * @param linkText - The text of the link
 */
export function trackOutboundLink(url: string, linkText?: string): void {
  if (!isAnalyticsEnabled()) {
    if (import.meta.env.DEV) {
      console.log('[Analytics] Outbound link (disabled):', { url, linkText });
    }
    return;
  }

  window.gtag?.('event', 'click', {
    event_category: 'outbound',
    event_label: url,
    link_text: linkText,
    transport_type: 'beacon',
  });
}

/**
 * Track scroll depth milestones
 * @param depth - The scroll depth percentage (25, 50, 75, 100)
 */
export function trackScrollDepth(depth: 25 | 50 | 75 | 100): void {
  if (!isAnalyticsEnabled()) return;

  window.gtag?.('event', 'scroll', {
    event_category: 'engagement',
    event_label: `${depth}%`,
    value: depth,
  });
}
